// ✅ MINIMAL CHANGES: WorkflowActionsStep - Only fix stepHelpers.setStepData calls

import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import {
  ChevronRight,
  Plus,
  Trash2,
  Mail,
  MessageSquare,
  Bell,
  Phone,
  Edit,
  Clock,
} from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { StepHelpers } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { useChannelType } from "@features/communication/messaging/hooks";
import {
  useMessageTemplates,
  useUpdateWorkflowActions,
} from "@features/ux/work-flow/hooks";
import { transformUIActionsToAPI } from "@features/ux/work-flow/utils";
import { UpdateWorkflowActionsRequest } from "@features/ux/work-flow/types";

// ... [Keep all existing constants and interfaces unchanged] ...

const sourceOptions = [
  { value: "inline", label: "Custom" },
  { value: "template", label: "Template" },
];

const delayUnitOptions = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
];

interface WorkflowAction {
  sequenceOrder: number;
  channelType: string;
  source: string;
  subject?: string;
  content?: string;
  templateId?: string;
  cc?: string;
  bcc?: string;
  delayValue?: number;
  delayUnit?: string;
  delay?: {
    value: number;
    unit: string;
  } | null;
}

interface WorkflowActionsStepProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
  existingWorkflow?: any;
  isEditMode?: boolean;
}

function getContentPlaceholder(channelType: string): string {
  switch (channelType) {
    case "EMAIL":
      return "Enter your email message content...";
    case "TEXT":
      return "Enter your SMS message (keep it concise)...";
    case "NOTIFICATION":
      return "Enter your push notification message...";
    case "WHATSAPP":
      return "Enter your voice message script...";
    default:
      return "Enter your message content...";
  }
}

const createActionFormFields = (
  readOnly: boolean = false,
  channelTypeOptions: any[] = [],
  templateOptions: any[] = [],
  isLoadingChannelTypes: boolean = false,
  isLoadingTemplates: boolean = false,
  context: { isNotFirstAction?: boolean } = {}
): FormField[] => [
  // Delay Value Field - Only shown for non-first actions
  {
    name: "delayValue",
    label: "Delay Duration",
    type: "number",
    required: true,
    placeholder: "Enter delay duration",
    helperText: "How long to wait before sending this message",
    showHelperTooltip: true,
    tooltipText:
      "Specify the amount of time to wait before sending this action",
    disabled: readOnly,
    min: 1,
    max: 999,
    showWhen: () => {
      return context?.isNotFirstAction === true;
    },
    classes: {
      container: "w-full mb-6",
      label: "block text-sm font-medium text-gray-700 mb-2",
      input: `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
        readOnly ? "bg-gray-50" : "bg-white"
      }`,
      error: "text-red-600 text-sm mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
    fullWidth: true,
  },
  // Delay Unit Field - Only shown for non-first actions
  {
    name: "delayUnit",
    label: "Delay Unit",
    type: "select",
    required: true,
    options: delayUnitOptions,
    placeholder: "Select time unit",
    helperText: "Time unit for the delay",
    showHelperTooltip: true,
    tooltipText: "Choose the time unit (minutes, hours, days, or weeks)",
    disabled: readOnly,
    showWhen: () => {
      return context?.isNotFirstAction === true;
    },
    classes: {
      container: "w-full mb-6",
      label: "block text-sm font-medium text-gray-700 mb-2",
      select: `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
        readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"
      }`,
      error: "text-red-600 text-sm mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
    fullWidth: true,
  },
  {
    name: "channelType",
    label: "Communication Channel",
    type: "select",
    required: true,
    options: channelTypeOptions,
    placeholder: isLoadingChannelTypes
      ? "Loading channels..."
      : "Select communication channel",
    helperText: "Choose the type of communication to send",
    showHelperTooltip: true,
    tooltipText: "Select the communication channel for this action",
    disabled: readOnly || isLoadingChannelTypes,
    classes: {
      container: "w-full mb-6",
      label: "block text-sm font-medium text-gray-700 mb-2",
      select: `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
        readOnly || isLoadingChannelTypes
          ? "bg-gray-50 cursor-not-allowed"
          : "bg-white"
      }`,
      error: "text-red-600 text-sm mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
    fullWidth: true,
  },
  {
    name: "source",
    label: "Content Source",
    type: "select",
    required: true,
    options: sourceOptions,
    placeholder: "Select content source",
    helperText: "Choose how you want to create the message content",
    showHelperTooltip: true,
    tooltipText: "Choose between writing custom content or using a template",
    disabled: readOnly,
    classes: {
      container: "w-full mb-6",
      label: "block text-sm font-medium text-gray-700 mb-2",
      select: `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
        readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"
      }`,
      error: "text-red-600 text-sm mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
    fullWidth: true,
  },
  // Template selection field - shown only when source is "template"
  {
    name: "templateId",
    label: "Select Template",
    type: "select",
    required: true,
    options: templateOptions,
    placeholder: isLoadingTemplates
      ? "Loading templates..."
      : templateOptions.length === 0
      ? "No templates available for this channel"
      : "Choose a template",
    helperText: "Select a pre-configured message template",
    showHelperTooltip: true,
    tooltipText:
      "Choose from available message templates for this channel type",
    disabled: readOnly || isLoadingTemplates || templateOptions.length === 0,
    showWhen: (formValues) => {
      return formValues.source === "template";
    },
    classes: {
      container: "w-full mb-6",
      label: "block text-sm font-medium text-gray-700 mb-2",
      select: `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
        readOnly || isLoadingTemplates || templateOptions.length === 0
          ? "bg-gray-50 cursor-not-allowed"
          : "bg-white"
      }`,
      error: "text-red-600 text-sm mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
    fullWidth: true,
  },
  // Email subject field - shown only for EMAIL channel and inline source
  {
    name: "subject",
    label: "Email Subject",
    type: "text",
    required: true,
    placeholder: "Enter email subject line",
    helperText: "Subject line for the email",
    showHelperTooltip: true,
    tooltipText: "Enter a clear and descriptive subject line",
    disabled: readOnly,
    showWhen: (formValues) => {
      const shouldShow =
        formValues.source === "inline" && formValues.channelType === "EMAIL";

      return shouldShow;
    },
    classes: {
      container: "w-full mb-6",
      label: "block text-sm font-medium text-gray-700 mb-2",
      input: `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
        readOnly ? "bg-gray-50" : "bg-white"
      }`,
      error: "text-red-600 text-sm mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
    fullWidth: true,
  },
  {
    name: "cc",
    label: "CC (Carbon Copy)",
    type: "text",
    required: false,
    placeholder: "Enter email addresses separated by commas",
    helperText: "Add email addresses for CC (comma-separated)",
    showHelperTooltip: true,
    tooltipText: "Enter multiple email addresses separated by commas",
    disabled: readOnly,
    showWhen: (formValues) => {
      return formValues.channelType === "EMAIL";
    },
    classes: {
      container: "w-full mb-6",
      label: "block text-sm font-medium text-gray-700 mb-2",
      input: `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
        readOnly ? "bg-gray-50" : "bg-white"
      }`,
      error: "text-red-600 text-sm mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
    fullWidth: true,
  },
  {
    name: "bcc",
    label: "BCC (Blind Carbon Copy)",
    type: "text",
    required: false,
    placeholder: "Enter email addresses separated by commas",
    helperText: "Add email addresses for BCC (comma-separated)",
    showHelperTooltip: true,
    tooltipText: "Enter multiple email addresses separated by commas",
    disabled: readOnly,
    showWhen: (formValues) => {
      return formValues.channelType === "EMAIL";
    },
    classes: {
      container: "w-full mb-6",
      label: "block text-sm font-medium text-gray-700 mb-2",
      input: `w-full h-12 px-4 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors ${
        readOnly ? "bg-gray-50" : "bg-white"
      }`,
      error: "text-red-600 text-sm mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
    fullWidth: true,
  },
  // Message content field - shown only for inline source
  {
    name: "content",
    label: "Message Content",
    type: "textarea",
    required: true,
    placeholder: "Enter your message content...", // Dynamic placeholder will be set via form updates
    rows: 4,
    helperText: "Enter the message content",
    showHelperTooltip: true,
    tooltipText: "Write the content for your message",
    disabled: readOnly,
    showWhen: (formValues) => {
      const shouldShow = formValues.source === "inline";

      return shouldShow;
    },
    classes: {
      container: "w-full mb-6",
      label: "block text-sm font-medium text-gray-700 mb-2",
      textarea: `w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors resize-vertical ${
        readOnly ? "bg-gray-50" : "bg-white"
      }`,
      error: "text-red-600 text-sm mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
    fullWidth: true,
  },
];

const WorkflowActionsStep: React.FC<WorkflowActionsStepProps> = ({
  stepHelpers,
  currentStepIndex = 2,
  onNext,
  onPrevious,
  readOnly = false,
  existingWorkflow,
  isEditMode,
}) => {
  const { toast } = useToast();
  const dataLoadedRef = useRef(false);
  const [actions, setActions] = useState<WorkflowAction[]>([]);
  const [editingActionId, setEditingActionId] = useState<string | null>(null);

  const channelTypesQuery = useChannelType();
  const channelTypes = channelTypesQuery.data || [];
  const isLoadingChannelTypes = channelTypesQuery.isLoading;

  const updateActionsMutation = useUpdateWorkflowActions();

  const defaultForm = FormService.createForm({
    fields: [],
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      channelType: "EMAIL",
      source: "inline",
      subject: "",
      content: "",
      templateId: "",
      cc: "",
      bcc: "",
      delayValue: 1,
      delayUnit: "hours",
    },
  });

  const [currentForm, setCurrentForm] = useState(defaultForm);

  const currentChannelType = currentForm.watch("channelType") || "EMAIL";
  const currentSource = currentForm.watch("source") || "inline";

  const templatesQuery = useMessageTemplates(currentChannelType);
  const templates = templatesQuery.data || [];
  const isLoadingTemplates = templatesQuery.isLoading;

  // ✅ FIX 1: ONLY save step data in controlled scenarios - SAME AS NewList
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      const stepData = {
        ...data,
        timestamp: new Date().toISOString(),
        stepIndex: currentStepIndex,
      };

      // ✅ Only call in controlled scenarios - NOT during render
      stepHelpers.setStepData(currentStepIndex, stepData);
    },
    [stepHelpers, currentStepIndex]
  );

  // ... [Keep all existing useMemo hooks for options unchanged] ...

  const channelTypeOptions = useMemo(() => {
    if (isLoadingChannelTypes) {
      return [{ value: "", label: "Loading channels..." }];
    }

    if (!channelTypes || channelTypes.length === 0) {
      return [{ value: "EMAIL", label: "Email" }];
    }

    return channelTypes.map((channelType: any) => ({
      value: channelType.key,
      label: channelType.key,
    }));
  }, [channelTypes, isLoadingChannelTypes]);

  const templateOptions = useMemo(() => {
    if (isLoadingTemplates) {
      return [{ value: "", label: "Loading templates..." }];
    }

    if (!templates || templates.length === 0) {
      return [];
    }

    return templates.map((template: any) => ({
      value: template.id.toString(),
      label: template.templateName,
      content: template.content,
      alias: template.templateAlias,
    }));
  }, [templates, isLoadingTemplates]);

  const channelTypeConfig = useMemo(
    () => ({
      EMAIL: { icon: Mail, label: "Email", color: "text-blue-600" },
      TEXT: { icon: MessageSquare, label: "SMS", color: "text-green-600" },
      NOTIFICATION: {
        icon: Bell,
        label: "Notification",
        color: "text-purple-600",
      },
      WHATSAPP: { icon: Phone, label: "WhatsApp", color: "text-orange-600" },
    }),
    []
  );

  const currentFormFields = useMemo(() => {
    const isNotFirstAction = actions.length > 0 || editingActionId !== "new";

    const fields = createActionFormFields(
      readOnly,
      channelTypeOptions,
      templateOptions,
      isLoadingChannelTypes,
      isLoadingTemplates,
      { isNotFirstAction }
    );

    const contentField = fields.find((field) => field.name === "content");
    if (contentField) {
      contentField.placeholder = getContentPlaceholder(currentChannelType);
    }

    return fields;
  }, [
    readOnly,
    channelTypeOptions,
    templateOptions,
    isLoadingChannelTypes,
    isLoadingTemplates,
    currentChannelType,
    actions.length,
    editingActionId,
  ]);

  const getTemplateName = useCallback(
    (templateId: string | number | undefined) => {
      if (!templateId) return "No template selected";

      const idString = templateId.toString();
      const template = templateOptions.find((t) => t.value === idString);
      if (template) {
        return template.label;
      }

      const allTemplates = [...(templates || [])];
      const foundTemplate = allTemplates.find(
        (t) => t.id.toString() === idString
      );

      return foundTemplate?.templateName || `Template ${templateId}`;
    },
    [templateOptions, templates]
  );

  const getTemplateContent = useCallback(
    (templateId: string | number | undefined) => {
      if (!templateId) return null;

      const idString = templateId.toString();
      const template = templateOptions.find((t) => t.value === idString);

      return template?.content || null;
    },
    [templateOptions]
  );

  // ✅ FIX 2: Data loading - SAME PATTERN AS NewList
  useEffect(() => {
    if (!dataLoadedRef.current) {
      try {
        let savedStepData = {};

        // ✅ Check stepHelpers first - SAME AS NewList
        if (stepHelpers) {
          savedStepData = stepHelpers.getStepData(currentStepIndex) || {};
        }

        // ✅ Check localStorage as fallback - SAME AS NewList
        if (
          Object.keys(savedStepData).length === 0 &&
          isEditMode &&
          existingWorkflow?.id
        ) {
          const storageKey = `workflow-builder-progress-${existingWorkflow.id}`;
          const progressData = localStorage.getItem(storageKey);
          if (progressData) {
            const parsed = JSON.parse(progressData);
            savedStepData = parsed.stepData?.[currentStepIndex] || {};
          }
        }

        if (
          savedStepData &&
          savedStepData.actions &&
          Array.isArray(savedStepData.actions)
        ) {
          setActions(savedStepData.actions);
        } else if (isEditMode && existingWorkflow && existingWorkflow.actions) {
          const transformedActions = (existingWorkflow.actions || []).map(
            (apiAction: any, index: number) => ({
              channelType:
                apiAction.channel_type ||
                apiAction.channelType ||
                apiAction.channel ||
                "EMAIL",
              channel:
                apiAction.channel_type ||
                apiAction.channelType ||
                apiAction.channel ||
                "EMAIL",
              subject:
                apiAction.subject ||
                apiAction.message_source?.content?.subject ||
                "",
              content:
                apiAction.content ||
                apiAction.message_source?.content?.body ||
                "",
              messageSource: {
                type: apiAction.message_source?.type || "CUSTOM",
                templateId: apiAction.message_source?.template_id || null,
                content: apiAction.message_source?.content || null,
              },
              sequenceOrder: apiAction.sequence_order || index + 1,
              source: apiAction.source || "inline",
              channelSpecificConfig: apiAction.channel_specific_config || {
                cc:
                  apiAction.channel_specific_config?.cc ||
                  apiAction.channelSpecificConfig?.cc ||
                  [],
                bcc:
                  apiAction.channel_specific_config?.bcc ||
                  apiAction.channelSpecificConfig?.bcc ||
                  [],
              },
              templateId: apiAction.template_id || apiAction.templateId,
              delayValue: apiAction.delay?.value || 1,
              delayUnit: apiAction.delay?.unit || "hours",
              delay: apiAction.delay || null,
            })
          );

          setActions(transformedActions);

          // ✅ Save step data AFTER transformation - SAME AS NewList
          const stepData = {
            actions: transformedActions,
            workflowId: existingWorkflow.id,
            timestamp: new Date().toISOString(),
            stepIndex: currentStepIndex,
          };
          saveStepData(stepData);
        }

        dataLoadedRef.current = true;
      } catch (error) {
        dataLoadedRef.current = true;
      }
    }
  }, [
    stepHelpers,
    currentStepIndex,
    isEditMode,
    existingWorkflow?.id,
    saveStepData,
  ]);

  useEffect(() => {
    if (existingWorkflow?.id) {
      dataLoadedRef.current = false;
    }
  }, [existingWorkflow?.id]);

  const handleAddAction = useCallback(() => {
    setEditingActionId("new");
  }, []);

  const handleEditAction = useCallback(
    (action: WorkflowAction, actionId: string) => {
      setEditingActionId(actionId);

      const formValues = {
        channelType: action.channelType || "EMAIL",
        source: action.source || "inline",
        subject: action.subject || "",
        content: action.content || "",
        templateId: action.templateId?.toString() || "",
        cc: action.cc || "",
        bcc: action.bcc || "",
        delayValue: action.delayValue || 1,
        delayUnit: action.delayUnit || "hours",
      };

      currentForm.reset(formValues);
    },
    [currentForm]
  );

  const handleDeleteAction = useCallback(
    (actionIndex: number) => {
      setActions((prev) => prev.filter((_, index) => index !== actionIndex));
      toast({
        title: "Action Removed",
        description: "The action has been removed from the workflow.",
      });
    },
    [toast]
  );

  const handleFormFieldChange = useCallback(
    (fieldName: string, value: any) => {
      const currentValues = currentForm.getValues();
      if (currentValues[fieldName] === value) {
        return;
      }

      if (fieldName === "source") {
        if (value === "template") {
          if (currentForm.getValues("subject")) {
            currentForm.setValue("subject", "");
          }
          if (currentForm.getValues("content")) {
            currentForm.setValue("content", "");
          }
        } else if (value === "inline") {
          if (currentForm.getValues("templateId")) {
            currentForm.setValue("templateId", "");
          }
        }
      }

      if (fieldName === "channelType") {
        if (value !== "EMAIL") {
          if (currentForm.getValues("subject")) {
            currentForm.setValue("subject", "");
          }
          if (currentForm.getValues("cc")) {
            currentForm.setValue("cc", "");
          }
          if (currentForm.getValues("bcc")) {
            currentForm.setValue("bcc", "");
          }
        }
      }
    },
    [currentForm]
  );

  // ✅ FIX 3: Save action - SAME PATTERN AS NewList
  const handleSaveAction = useCallback(
    async (formData: any) => {
      try {
        const isValid = await FormService.validateForm(currentForm);

        if (!isValid) {
          toast({
            title: "Please complete all required fields for this action.",
            variant: "destructive",
          });
          return;
        }

        const currentFormValues = currentForm.getValues();
        const isNotFirstAction =
          actions.length > 0 || editingActionId !== "new";

        const payload: WorkflowAction = {
          sequenceOrder: actions.length + 1,
          channel: currentFormValues.channelType,
          channelType: currentFormValues.channelType,
          source: currentFormValues.source,

          ...(currentFormValues.channelType === "EMAIL" &&
            currentFormValues.source === "inline" && {
              subject: currentFormValues.subject || "",
            }),
          ...(currentFormValues.source === "inline" && {
            content: currentFormValues.content || "",
          }),
          ...(currentFormValues.source === "template" && {
            templateId: currentFormValues.templateId
              ? parseInt(currentFormValues.templateId)
              : undefined,
          }),

          ...(isNotFirstAction && {
            delayValue: parseInt(currentFormValues.delayValue) || 1,
            delayUnit: currentFormValues.delayUnit || "hours",
            delay: {
              value: parseInt(currentFormValues.delayValue) || 1,
              unit: currentFormValues.delayUnit || "hours",
            },
          }),

          messageSource: {
            type:
              currentFormValues.source === "template" ? "TEMPLATE" : "CUSTOM",
            templateId:
              currentFormValues.source === "template" &&
              currentFormValues.templateId
                ? parseInt(currentFormValues.templateId)
                : null,
            content:
              currentFormValues.source === "inline"
                ? (() => {
                    const content: any = {
                      body: currentFormValues.content || "",
                    };

                    if (
                      currentFormValues.channelType === "EMAIL" &&
                      currentFormValues.subject
                    ) {
                      content.subject = currentFormValues.subject;
                    }

                    return content;
                  })()
                : null,
          },
          channelSpecificConfig: {
            cc:
              currentFormValues.channelType === "EMAIL" && currentFormValues.cc
                ? currentFormValues.cc
                    .split(",")
                    .map((email: string) => email.trim())
                    .filter((email: string) => email.length > 0)
                : [],
            bcc:
              currentFormValues.channelType === "EMAIL" && currentFormValues.bcc
                ? currentFormValues.bcc
                    .split(",")
                    .map((email: string) => email.trim())
                    .filter((email: string) => email.length > 0)
                : [],
          },
          timeDelay: undefined,
        };

        let updatedActions = [...actions];

        if (editingActionId === "new") {
          updatedActions.push(payload);
          toast({
            title: "Action Added",
            description: "New action has been added to the workflow.",
          });
        } else if (editingActionId !== null) {
          const index = parseInt(editingActionId);
          if (index >= 0 && index < updatedActions.length) {
            updatedActions[index] = {
              ...updatedActions[index],
              ...payload,
            };
            toast({
              title: "Action Updated",
              description: "The action has been updated successfully.",
            });
          } else {
            toast({
              title: "Error updating action",
              description: "Invalid action index. Please try again.",
              variant: "destructive",
            });
            return;
          }
        }

        setActions(updatedActions);

        // ✅ Save step data AFTER updating actions - SAME AS NewList
        saveStepData({ actions: updatedActions });
        setEditingActionId(null);

        currentForm.reset({
          channelType: "EMAIL",
          source: "inline",
          subject: "",
          content: "",
          templateId: "",
          cc: "",
          bcc: "",
          delayValue: 1,
          delayUnit: "hours",
        });
      } catch (error) {
        toast({
          title: "Error saving action",
          description: "Please check the action configuration and try again.",
          variant: "destructive",
        });
      }
    },
    [currentForm, editingActionId, actions, toast, saveStepData]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingActionId(null);
    currentForm.reset({
      channelType: "EMAIL",
      source: "inline",
      subject: "",
      content: "",
      templateId: "",
      cc: "",
      bcc: "",
      delayValue: 1,
      delayUnit: "hours",
    });
  }, [currentForm]);

  const getWorkflowId = (): string | null => {
    if (existingWorkflow?.id) {
      return existingWorkflow.id;
    }

    if (stepHelpers) {
      try {
        const step0Data = stepHelpers.getStepData(0);
        if (step0Data?.workflowId) {
          return step0Data.workflowId;
        }
      } catch (error) {
        toast({
          title: "Error",
          description: error?.message,
          variant: "destructive",
        });
      }
    }

    try {
      const storageKey = "workflow-builder-progress";
      const progressData = localStorage.getItem(storageKey);

      if (progressData) {
        const parsed = JSON.parse(progressData);
        return parsed.stepData?.[0]?.workflowId || null;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error?.message,
        variant: "destructive",
      });
    }

    return null;
  };

  const updateActionsAPI = useCallback(
    async (actionsData: WorkflowAction[]) => {
      try {
        const workflowId = getWorkflowId();
        if (!workflowId) {
          toast({
            title: "Error",
            description: "No workflow ID found",
            variant: "destructive",
          });
          return { success: false, error: "Missing workflow ID" };
        }

        if (actionsData.length === 0) {
          toast({
            title: "Error",
            description: "No actions found",
            variant: "destructive",
          });
          return { success: false, error: "No actions to update" };
        }

        const transformedActions = transformUIActionsToAPI(actionsData);
        const actionsPayload: UpdateWorkflowActionsRequest = {
          processActions: true,
          isDraft: false,
          actions: transformedActions,
        };

        await updateActionsMutation.mutateAsync({
          workflowId: parseInt(workflowId.toString()),
          actionsData: actionsPayload,
        });

        const title = isEditMode ? "Actions Updated" : "Actions Saved";
        const description = isEditMode
          ? "Workflow actions updated successfully."
          : "Workflow actions saved as draft.";

        toast({ title, description });
        return { success: true };
      } catch (error: any) {
        const errorMessage = error?.message || "Failed to save actions data";
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return { success: false, error: errorMessage };
      }
    },
    [
      existingWorkflow?.id,
      stepHelpers,
      updateActionsMutation,
      toast,
      isEditMode,
      transformUIActionsToAPI,
    ]
  );

  // ✅ FIX 4: Handle next - SAME PATTERN AS NewList
  const handleNext = useCallback(async () => {
    if (actions.length === 0) {
      toast({
        title: "Actions Required",
        description: "Please add at least one action before proceeding.",
        variant: "destructive",
      });
      return;
    }

    try {
      // ✅ Call API first - SAME AS NewList pattern
      const result = await updateActionsAPI(actions);
      if (!result.success) {
        return;
      }

      // ✅ Save step data AFTER API success - SAME AS NewList
      if (stepHelpers) {
        const currentData = stepHelpers.getStepData(currentStepIndex) || {};
        const stepData = {
          ...currentData,
          actions: actions,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
      }

      if (onNext) {
        onNext();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Please check your configuration and try again.",
        variant: "destructive",
      });
    }
  }, [
    actions.length,
    actions,
    updateActionsAPI,
    onNext,
    toast,
    stepHelpers,
    currentStepIndex,
  ]);

  const renderCustomFooter = useCallback(() => {
    if (!editingActionId) return null;

    return (
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <Button variant="outline" onClick={handleCancelEdit} type="button">
          Cancel
        </Button>
        <Button type="submit">
          {editingActionId === "new" ? "Add Action" : "Update Action"}
        </Button>
      </div>
    );
  }, [editingActionId, handleCancelEdit]);

  const formatDelay = (delayValue: number, delayUnit: string) => {
    return `${delayValue} ${delayUnit}`;
  };

  // ... [Keep renderActionCard function unchanged - it's just display logic] ...

  const renderActionCard = useCallback(
    (action: WorkflowAction, index: number) => {
      const channelType = action.channelType || "EMAIL";
      const config =
        channelTypeConfig[channelType as keyof typeof channelTypeConfig];

      if (!config) {
        return (
          <Card key={index} className="relative border-red-200">
            <CardContent className="p-4">
              <p className="text-red-600">
                Error: Unknown channel type "{channelType}"
              </p>
              {!readOnly && (
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditAction(action, index.toString())}
                    type="button"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Fix
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAction(index)}
                    className="text-red-600 hover:text-red-700"
                    type="button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      }

      const IconComponent = config.icon;
      const isFirstAction = index === 0;

      return (
        <Card key={index} className="relative">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-gray-100 ${config.color}`}>
                  <IconComponent className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    {config.label}
                    {!isFirstAction && action.delay && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                        <Clock className="h-3 w-3" />
                        {formatDelay(action.delay.value, action.delay.unit)}
                      </span>
                    )}
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    {action.source === "template"
                      ? "Template"
                      : "Custom Message"}
                    {!isFirstAction && " • Delayed"}
                  </p>
                </div>
              </div>
              {!readOnly && (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditAction(action, index.toString())}
                    type="button"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAction(index)}
                    className="text-red-600 hover:text-red-700"
                    type="button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!isFirstAction && action.delay && (
              <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-orange-600" />
                  <span className="font-medium text-orange-800">
                    Delay: {formatDelay(action.delay.value, action.delay.unit)}
                  </span>
                </div>
                <p className="text-xs text-orange-600 mt-1">
                  This action will be sent{" "}
                  {formatDelay(action.delay.value, action.delay.unit)} after the
                  previous action
                </p>
              </div>
            )}

            {action.source === "template" ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Template:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {getTemplateName(action.templateId)}
                  </span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-600">
                    ID: {action.templateId}
                  </span>
                </div>

                {(() => {
                  const templateContent = getTemplateContent(action.templateId);
                  if (templateContent) {
                    return (
                      <div className="rounded-lg p-3">
                        <div className="space-y-3">
                          {action.channelType === "EMAIL" &&
                            templateContent.subject && (
                              <div>
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                  Subject
                                </span>
                                <div className="text-sm font-semibold text-gray-900 mt-1">
                                  {templateContent.subject}
                                </div>
                              </div>
                            )}

                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Content
                            </span>
                            <div className="text-sm mt-1 whitespace-pre-wrap text-gray-000 bg-white p-3 rounded border line-clamp-4">
                              {templateContent.body}
                            </div>
                          </div>

                          <div className="text-xs text-blue-600">
                            Character count: {templateContent.body?.length || 0}
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                        <div className="text-sm text-yellow-800">
                          ⚠️ Template content not available. Template ID:{" "}
                          {action.templateId}
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            ) : (
              <div className="space-y-2">
                {action.subject && (
                  <div>
                    <p className="text-xs font-medium text-gray-500">
                      Subject:
                    </p>
                    <p className="text-sm text-gray-900">{action.subject}</p>
                  </div>
                )}
                {action.cc && (
                  <div>
                    <p className="text-xs font-medium text-gray-500">CC:</p>
                    <p className="text-sm text-gray-900">{action.cc}</p>
                  </div>
                )}
                {action.bcc && (
                  <div>
                    <p className="text-xs font-medium text-gray-500">BCC:</p>
                    <p className="text-sm text-gray-900">{action.bcc}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-gray-500">Content:</p>
                  <p className="text-sm text-gray-900 line-clamp-3">
                    {action.content || "No content specified"}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    },
    [
      channelTypeConfig,
      readOnly,
      handleEditAction,
      handleDeleteAction,
      getTemplateName,
      getTemplateContent,
    ]
  );

  if (isLoadingChannelTypes && channelTypes.length === 0) {
    return (
      <div className="space-y-6 pt-4">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading communication channels...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-4">
      <div>
        <p className="text-gray-600">
          Define the communication actions that will be executed when this
          workflow is triggered. You can send emails, SMS messages, push
          notifications, or voice calls. Actions after the first one can be
          delayed.
        </p>
      </div>

      {actions.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-4">
            Configured Actions ({actions.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {actions.map(renderActionCard)}
          </div>
        </div>
      )}

      {!readOnly && !editingActionId && (
        <div className="text-center">
          <Button
            onClick={handleAddAction}
            variant="outline"
            className="w-full max-w-md h-20 border-dashed border-2 border-blue-300 text-blue-600 hover:bg-blue-50"
            type="button"
            disabled={isLoadingChannelTypes}
          >
            <Plus className="h-6 w-6 mr-2" />
            {isLoadingChannelTypes ? "Loading..." : "Add Communication Action"}
          </Button>
        </div>
      )}

      {editingActionId && !readOnly && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {editingActionId === "new" ? "Add New Action" : "Edit Action"}
              {(actions.length > 0 || editingActionId !== "new") && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                  <Clock className="h-3 w-3" />
                  Delayed Action
                </span>
              )}
            </CardTitle>
            {(actions.length > 0 || editingActionId !== "new") && (
              <p className="text-sm text-orange-600">
                This action will include delay settings since it's not the first
                action.
              </p>
            )}
            {currentSource === "template" && isLoadingTemplates && (
              <div className="text-sm text-blue-600 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                Loading templates for {currentChannelType}...
              </div>
            )}
          </CardHeader>
          <CardContent>
            <DynamicForm
              fields={currentFormFields}
              form={currentForm}
              onSubmit={handleSaveAction}
              onFieldChange={handleFormFieldChange}
              config={{
                showSubmitButton: false,
                showResetButton: false,
                classes: {
                  form: "space-y-6",
                  button: "px-4 py-2 rounded-md font-medium transition-colors",
                  buttonContainer:
                    "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  container: "space-y-1",
                },
              }}
              gridColumns={1}
              responsiveColumns={{ sm: 1, md: 1 }}
              renderCustomFooter={renderCustomFooter}
              className="space-y-6"
            />
          </CardContent>
        </Card>
      )}

      {actions.length === 0 && !editingActionId && (
        <div className="text-center py-12">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <MessageSquare className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Actions Added
          </h3>
          <p className="text-gray-500 mb-6">
            Add communication actions to define what happens when this workflow
            triggers. The first action sends immediately, subsequent actions can
            be delayed.
          </p>
        </div>
      )}

      <div className="pt-6 flex justify-between border-t border-gray-200">
        <Button
          variant="outline"
          onClick={onPrevious}
          type="button"
          className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={
            actions.length === 0 ||
            isLoadingChannelTypes ||
            updateActionsMutation.isPending
          }
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          type="button"
        >
          {updateActionsMutation.isPending ? "Saving..." : "Submit"}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default WorkflowActionsStep;
